import {useQuery, type QueryClient} from '@github-ui/react-query'
import type {CopilotChatRepo} from '@github-ui/copilot-chat-types'

import {fetchRepo} from './fetch-copilot-chat-repo'

export const REPOSITORY_POLICY_MESSAGE =
  'Copilot is unavailable for this repository because of organization-specific policies.'

interface RepositoryPolicyOptions {
  revalidationKey?: string
  refetchOnMount?: boolean | 'always'
  staleTime?: number
}

export function repositoryPolicyQueryKey(repository: number | string | undefined, revalidationKey?: string) {
  return ['copilot-repository-policy', repository, revalidationKey ?? null] as const
}

export function useRepositoryPolicy(
  repository: number | string | undefined,
  {revalidationKey, refetchOnMount, staleTime}: RepositoryPolicyOptions = {},
) {
  return useQuery({
    queryKey: repositoryPolicyQueryKey(repository, revalidationKey),
    queryFn: () => {
      if (!repository) throw new Error('No repository selected')
      return fetchRepo(repository)
    },
    enabled: Boolean(repository),
    ...(refetchOnMount === undefined ? {} : {refetchOnMount}),
    ...(staleTime === undefined ? {} : {staleTime}),
  })
}

export async function revalidateRepositoryPolicy(
  queryClient: QueryClient,
  repository: number | string,
  revalidationKey: string,
): Promise<CopilotChatRepo | undefined> {
  const queryKey = repositoryPolicyQueryKey(repository, revalidationKey)
  try {
    await queryClient.invalidateQueries({queryKey, exact: true, refetchType: 'active'})
  } catch {
    return undefined
  }

  const state = queryClient.getQueryState<CopilotChatRepo>(queryKey)
  return state?.status === 'success' ? state.data : undefined
}
